import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import TrailTime from '../../components/TrailTime'
import { mutedTextClass, StatusPill } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { trailActor } from '../../lib/trailEntry'

interface LeaveEvent {
  id: string
  at: string
  actor_id: string | null
  actor_kind: string
  actor_name: string
  stage: string
  action: string
  verdict: string
  status_before: string
  status_after: string
  comment: string
}

function useTrailEvents(queryKey: readonly string[], url: string) {
  return useQuery({
    queryKey,
    queryFn: async () => (await api.get<LeaveEvent[]>(url)).data,
  })
}

function Actor({ entry }: { entry: LeaveEvent }) {
  const { t } = useTranslation()
  const actor = trailActor(entry)
  if (actor.kind === 'system') {
    return (
      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-500/20 dark:text-sky-300">
        {t('audit.documentTrail.systemActor')}
      </span>
    )
  }
  if (actor.kind === 'unknown') return null
  return <span className="font-medium text-slate-700 dark:text-slate-200">{actor.name}</span>
}

export function TrailPanel({
  queryKey,
  url,
  stagePrefix,
}: {
  queryKey: readonly string[]
  url: string
  stagePrefix: string
}) {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useTrailEvents(queryKey, url)

  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('leave.trail.title')}</h3>
      {isError ? (
        <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {errorMessage(error) || t('leave.trail.error')}
        </p>
      ) : isLoading ? (
        <p className={`text-xs ${mutedTextClass}`}>{t('audit.loading')}</p>
      ) : !data || data.length === 0 ? (
        <p className={`text-xs ${mutedTextClass}`}>{t('leave.trail.empty')}</p>
      ) : (
        <ol className="space-y-2">
          {data.map((entry) => (
            <li key={entry.id} className="rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-slate-700 dark:text-slate-200">
                  {t(`audit.personTrail.action.${entry.action}`, entry.action)}
                </span>
                {entry.stage && (
                  <span className={mutedTextClass}>{t(`${stagePrefix}.${entry.stage}`, entry.stage)}</span>
                )}
                {entry.verdict && <StatusPill status={entry.verdict} />}
                <Actor entry={entry} />
                <TrailTime entry={entry} className={`ml-auto ${mutedTextClass}`} />
              </div>
              {entry.comment && (
                <p className="mt-1 whitespace-pre-line text-slate-600 dark:text-slate-300">{entry.comment}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

export function useLeaveEvents(leaveId: string) {
  return useTrailEvents(['time', 'leave', leaveId, 'events'], `/api/time/leave/${leaveId}/events`)
}

export default function LeaveTrail({ leaveId }: { leaveId: string }) {
  return (
    <TrailPanel
      queryKey={['time', 'leave', leaveId, 'events']}
      url={`/api/time/leave/${leaveId}/events`}
      stagePrefix="leave.stage"
    />
  )
}

export function DayTrail({ dayId }: { dayId: string }) {
  return (
    <TrailPanel
      queryKey={['time', 'days', dayId, 'events']}
      url={`/api/time/days/${dayId}/events`}
      stagePrefix="time.cosign.stage"
    />
  )
}
